import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Printer, Save, Mail, Plus, Trash2, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { PROFILE_BY_LEVEL, VAR_CEILING, riskStatus, trackingError } from "@/lib/risk-policy";
import { format } from "date-fns";

interface Props { range: 'P' | 'R' }

interface MetricRow { portfolio_range: 'P'|'R'; portfolio_level: number; metric: string; portfolio_value: number | null; benchmark_value: number | null; as_of_date: string | null; observations: number | null }
interface Scenario { id: string; name: string; equity_dd_pct: number; credit_dd_pct: number }
interface Commentary { quarter_label: string; portfolio_range: string; portfolio_level: number; commentary: string }
interface TECeiling { profile: string; ceiling_pct: number }
interface Recipient { id: string; email: string; role_label: string; is_active: boolean }

// Reporting cadence: meetings happen in 1st month of each calendar quarter
// and review the previous 3 calendar months (e.g. Jan meeting → Q4 prior year).
function reportingQuarter(d = new Date()): string {
  const m = d.getMonth(); // 0-11
  const y = d.getFullYear();
  // If today is in 1st month of a quarter (Jan/Apr/Jul/Oct), look back at previous quarter
  const inFirstMonth = m % 3 === 0;
  const refMonth = inFirstMonth ? m - 1 : m; // any month inside the quarter being reviewed
  let qYear = y;
  let qMonth = refMonth;
  if (qMonth < 0) { qMonth = 11; qYear = y - 1; }
  const q = Math.floor(qMonth / 3) + 1;
  return `${qYear} Q${q}`;
}

function quarterMonths(label: string): string {
  const m = label.match(/^(\d{4}) Q([1-4])$/);
  if (!m) return '';
  const y = Number(m[1]); const q = Number(m[2]);
  const start = new Date(y, (q - 1) * 3, 1);
  const end = new Date(y, q * 3, 0);
  return `${format(start, 'MMM')}–${format(end, 'MMM yyyy')}`;
}

function autoCommentary(args: {
  range: 'P'|'R'; level: number; profile: string;
  portVar: number|null; ceiling: number; te: number|null; teCeiling: number;
  status: 'within'|'amber'|'breach'|'no-data';
}): string {
  const { range, level, profile, portVar, ceiling, te, teCeiling, status } = args;
  const id = `${range}${level} (${profile})`;
  const varText = portVar != null
    ? `1-year 95% VaR was ${portVar.toFixed(2)}% against the ${ceiling.toFixed(1)}% ceiling (${((portVar/ceiling)*100).toFixed(0)}% of limit)`
    : `VaR data was not available for the period`;
  const teText = te != null
    ? `Tracking Error stood at ${te.toFixed(2)}% versus the ${teCeiling.toFixed(1)}% ceiling`
    : `Tracking Error was not yet computable for this portfolio`;
  const statusText = status === 'breach'
    ? 'The portfolio was in breach of policy and remediation is required.'
    : status === 'amber'
    ? 'The portfolio is within policy but elevated; the SPM continues to monitor closely.'
    : status === 'within'
    ? 'The portfolio operated comfortably inside policy limits.'
    : 'Insufficient data to assess against policy this quarter.';
  return `${id}: ${varText}; ${teText}. ${statusText}`;
}

export default function BoardPackPanel({ range }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [quarter, setQuarter] = useState<string>(reportingQuarter());

  const { data: metrics = [] } = useQuery({
    queryKey: ['risk-metrics-cache', range],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_metrics_cache').select('*').eq('portfolio_range', range);
      if (error) throw error;
      return data as MetricRow[];
    },
  });

  // Daily values — same source the VaR page uses. We compute Tracking Error
  // client-side from these so the Board pack never depends on whether the
  // sync function happened to cache `tracking_error_1y`.
  const { data: daily = [] } = useQuery({
    queryKey: ['risk-daily', range],
    queryFn: async () => {
      const all: { portfolio_level: number; series_type: 'portfolio'|'benchmark'; value_date: string; value: number }[] = [];
      const pageSize = 1000;
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('risk_daily_values')
          .select('portfolio_level, series_type, value_date, value')
          .eq('portfolio_range', range)
          .order('value_date', { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all.push(...(data as any));
        if (data.length < pageSize) break;
        from += pageSize;
      }
      return all;
    },
  });

  const teByLevel = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const level of [1,2,3,4,5]) {
      // Build date-aligned arrays so portfolio[i] and benchmark[i] share the same date
      const pMap = new Map<string, number>();
      const bMap = new Map<string, number>();
      for (const d of daily) {
        if (d.portfolio_level !== level) continue;
        if (d.series_type === 'portfolio') pMap.set(d.value_date, Number(d.value));
        else bMap.set(d.value_date, Number(d.value));
      }
      const dates = Array.from(pMap.keys()).filter(k => bMap.has(k)).sort();
      // Trailing 1-year window — 365 calendar days (CBAM Perf sheet stores
      // one row per calendar day, not per trading day).
      const window = dates.slice(-365);
      const p = window.map(d => pMap.get(d)!);
      const b = window.map(d => bMap.get(d)!);
      m.set(level, trackingError(p, b));
    }
    return m;
  }, [daily]);

  const { data: scenarios = [] } = useQuery({
    queryKey: ['risk-scenarios'],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_stress_scenarios').select('*').order('start_date');
      if (error) throw error;
      return data as Scenario[];
    },
  });
  const { data: portfolioStress = [] } = useQuery({
    queryKey: ['risk-scenario-portfolio-stress', range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('risk_scenario_portfolio_stress' as any)
        .select('scenario_id, portfolio_level, stressed_loss_pct')
        .eq('portfolio_range', range);
      if (error) throw error;
      return (data ?? []) as unknown as { scenario_id: string; portfolio_level: number; stressed_loss_pct: number | null }[];
    },
  });
  const { data: settings } = useQuery({
    queryKey: ['risk-stress-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_stress_settings').select('*').single();
      if (error) throw error;
      return data as { materially_exceeded_multiplier: number };
    },
  });
  const { data: teCeilings = [] } = useQuery({
    queryKey: ['risk-te-ceilings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_te_ceilings' as any).select('profile, ceiling_pct');
      if (error) throw error;
      return (data ?? []) as unknown as TECeiling[];
    },
  });
  const { data: recipients = [], refetch: refetchRecipients } = useQuery({
    queryKey: ['risk-board-recipients'],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_board_recipients' as any).select('*').order('email');
      if (error) throw error;
      return (data ?? []) as unknown as Recipient[];
    },
  });

  const { data: commentaries = [] } = useQuery({
    queryKey: ['risk-board-commentary', range, quarter],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_board_commentary').select('*').eq('portfolio_range', range).eq('quarter_label', quarter);
      if (error) throw error;
      return data as Commentary[];
    },
  });

  const teCeilingByProfile = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of teCeilings) m.set(x.profile, Number(x.ceiling_pct));
    return m;
  }, [teCeilings]);

  const stressByLevelScenario = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const s of portfolioStress) m.set(`${s.portfolio_level}|${s.scenario_id}`, s.stressed_loss_pct);
    return m;
  }, [portfolioStress]);

  const multiplier = settings?.materially_exceeded_multiplier ?? 1.5;

  const rows = useMemo(() => {
    return [1,2,3,4,5].map(level => {
      const profile = PROFILE_BY_LEVEL[level];
      const ceiling = VAR_CEILING[profile];
      const teCeil = teCeilingByProfile.get(profile) ?? 0;
      const varRow = metrics.find(x => x.portfolio_level === level && x.metric === 'var_1y_95');
      const teRow = metrics.find(x => x.portfolio_level === level && x.metric === 'tracking_error_1y');
      const portVar = varRow?.portfolio_value ?? null;
      const benchVar = varRow?.benchmark_value ?? null;
      // Prefer the client-side computation (matches the VaR page's data source);
      // fall back to the cached metric if daily values aren't loaded yet.
      const te = teByLevel.get(level) ?? teRow?.portfolio_value ?? null;
      const status = riskStatus(portVar, ceiling);

      const stressBy: Record<string, number | null> = {};
      for (const sc of scenarios) {
        stressBy[sc.name] = stressByLevelScenario.get(`${level}|${sc.id}`) ?? null;
      }
      return { level, profile, ceiling, teCeil, portVar, benchVar, te, status, asOf: varRow?.as_of_date ?? null, stressBy };
    });
  }, [metrics, scenarios, stressByLevelScenario, teCeilingByProfile, teByLevel]);


  // Initialise commentary: saved value if present, otherwise auto-boilerplate
  const [localCommentary, setLocalCommentary] = useState<Record<number, string>>({});
  useEffect(() => {
    const m: Record<number, string> = {};
    for (const r of rows) {
      const saved = commentaries.find(c => c.portfolio_level === r.level)?.commentary;
      m[r.level] = saved && saved.trim().length > 0
        ? saved
        : autoCommentary({ range, level: r.level, profile: r.profile, portVar: r.portVar, ceiling: r.ceiling, te: r.te, teCeiling: r.teCeil, status: r.status as any });
    }
    setLocalCommentary(m);
  }, [commentaries, rows, range]);

  const saveCommentary = useMutation({
    mutationFn: async (level: number) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('risk_board_commentary').upsert({
        quarter_label: quarter,
        portfolio_range: range,
        portfolio_level: level,
        commentary: localCommentary[level] ?? '',
        updated_at: new Date().toISOString(),
        updated_by: user?.id,
      }, { onConflict: 'quarter_label,portfolio_range,portfolio_level' });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Saved' });
      qc.invalidateQueries({ queryKey: ['risk-board-commentary', range, quarter] });
    },
    onError: (e: any) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  const sendToCompliance = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('board-pack-notify', {
        body: { range, quarter },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (d: any) => toast({ title: 'Sent to Compliance', description: `${d?.sent ?? 0} recipient(s) emailed` }),
    onError: (e: any) => toast({ title: 'Send failed', description: e.message, variant: 'destructive' }),
  });

  // Recipient management
  const [newEmail, setNewEmail] = useState('');
  const addRecipient = async () => {
    if (!newEmail) return;
    const { error } = await supabase.from('risk_board_recipients' as any).insert({ email: newEmail, role_label: 'Compliance', is_active: true });
    if (error) return toast({ title: 'Add failed', description: error.message, variant: 'destructive' });
    setNewEmail(''); refetchRecipients();
  };
  const removeRecipient = async (id: string) => {
    const { error } = await supabase.from('risk_board_recipients' as any).delete().eq('id', id);
    if (error) return toast({ title: 'Remove failed', description: error.message, variant: 'destructive' });
    refetchRecipients();
  };

  return (
    <div className="space-y-6">
      <Card className="print:hidden">
        <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>Board Pack — {range} Portfolios</CardTitle>
            <CardDescription>
              Tier 1 §11.6 quarterly Board reporting. Default quarter is the calendar quarter just ended (review window {quarterMonths(quarter)}).
            </CardDescription>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1.5">
              <Label className="text-xs">Quarter</Label>
              <Input value={quarter} onChange={e => setQuarter(e.target.value)} className="w-32" />
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline"><Settings className="h-4 w-4 mr-2" />Recipients</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Compliance distribution list</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input placeholder="email@firm.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
                    <Button onClick={addRecipient}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1">
                    {recipients.map(r => (
                      <div key={r.id} className="flex items-center justify-between border rounded px-2 py-1 text-sm">
                        <span>{r.email}<span className="text-muted-foreground ml-2">({r.role_label})</span></span>
                        <Button size="sm" variant="ghost" onClick={() => removeRecipient(r.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    ))}
                    {recipients.length === 0 && <p className="text-sm text-muted-foreground">No recipients configured.</p>}
                  </div>
                  <p className="text-xs text-muted-foreground">Recipients receive the quarterly Board Pack summary by email automatically on the 1st of Jan/Apr/Jul/Oct, and whenever you click "Send to Compliance".</p>
                </div>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={() => sendToCompliance.mutate()} disabled={sendToCompliance.isPending}>
              <Mail className="h-4 w-4 mr-2" />Send to Compliance
            </Button>
            <Button size="sm" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Download / Print PDF
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Print-optimised board pack */}
      <div className="board-pack space-y-6">
        <div className="flex items-center justify-between border-b pb-3">
          <div>
            <h1 className="text-2xl font-semibold">CBAM Risk Board Pack — {range} Portfolios</h1>
            <p className="text-sm text-muted-foreground">{quarter} ({quarterMonths(quarter)}) · Generated {format(new Date(), 'dd/MM/yyyy')}</p>
          </div>
          <Badge variant="outline">Tier 1 §11.6</Badge>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Value-at-Risk vs Ceiling (Tier 1 §11.2)</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Portfolio</TableHead><TableHead>Profile</TableHead>
                <TableHead className="text-right">Portfolio VaR</TableHead>
                <TableHead className="text-right">Benchmark VaR</TableHead>
                <TableHead className="text-right">Ceiling</TableHead>
                <TableHead className="text-right">% of Ceiling</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>As of</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.level}>
                    <TableCell className="font-medium">{range}{r.level}</TableCell>
                    <TableCell>{r.profile}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.portVar != null ? r.portVar.toFixed(2) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.benchVar != null ? r.benchVar.toFixed(2) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.ceiling.toFixed(1)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.portVar != null ? `${((r.portVar/r.ceiling)*100).toFixed(0)}%` : '—'}</TableCell>
                    <TableCell>
                      {r.status === 'breach' && <Badge variant="destructive">Breach</Badge>}
                      {r.status === 'amber' && <Badge className="bg-amber-500 text-white">Amber</Badge>}
                      {r.status === 'within' && r.portVar != null && <Badge variant="secondary">Within</Badge>}
                      {r.portVar == null && <Badge variant="outline">No data</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.asOf ? format(new Date(r.asOf), 'dd/MM/yyyy') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Tracking Error section with bar chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tracking Error vs Ceiling (Tier 1 §11.4)</CardTitle>
            <CardDescription>1-year ex-post TE, annualised from daily portfolio vs benchmark log returns.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rows.map(r => {
                const pct = r.te != null && r.teCeil > 0 ? Math.min(150, (r.te / r.teCeil) * 100) : 0;
                const breach = r.te != null && r.te > r.teCeil;
                const amber = r.te != null && !breach && r.te > r.teCeil * 0.8;
                return (
                  <div key={r.level} className="grid grid-cols-12 items-center gap-3 text-sm">
                    <div className="col-span-2 font-medium">{range}{r.level} <span className="text-muted-foreground font-normal">{r.profile}</span></div>
                    <div className="col-span-7 relative h-5 bg-muted rounded overflow-hidden">
                      <div className={`h-full ${breach ? 'bg-destructive' : amber ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
                      <div className="absolute top-0 bottom-0 border-r-2 border-foreground/40" style={{ left: '66.66%' }} title={`Ceiling ${r.teCeil}%`} />
                    </div>
                    <div className="col-span-3 text-right tabular-nums">
                      <span className={breach ? 'text-destructive font-semibold' : amber ? 'text-amber-600' : ''}>
                        {r.te != null ? `${r.te.toFixed(2)}%` : '—'}
                      </span>
                      <span className="text-muted-foreground"> / {r.teCeil.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stress Replay (Tier 1 §11.5)</CardTitle>
            <CardDescription>Materially exceeded threshold: {multiplier}× VaR ceiling.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Portfolio</TableHead>
                {scenarios.map(s => <TableHead key={s.id} className="text-right">{s.name}</TableHead>)}
                <TableHead className="text-right">Ceiling</TableHead>
                <TableHead className="text-right">Red @ ≥</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.level}>
                    <TableCell className="font-medium">{range}{r.level} {r.profile}</TableCell>
                    {scenarios.map(s => {
                      const v = r.stressBy[s.name];
                      if (v == null) return <TableCell key={s.id} className="text-right text-muted-foreground">—</TableCell>;
                      const red = v > r.ceiling * multiplier;
                      const amber = !red && v > r.ceiling;
                      return (
                        <TableCell key={s.id} className={`text-right tabular-nums ${red ? 'text-destructive font-semibold' : amber ? 'text-amber-600' : ''}`}>
                          {v.toFixed(2)}%
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right tabular-nums">{r.ceiling.toFixed(1)}%</TableCell>
                    <TableCell className="text-right tabular-nums">{(r.ceiling*multiplier).toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Commentary by Portfolio</CardTitle>
            <CardDescription className="print:hidden">Auto-generated text appears as a starting point; edit and Save to override for this quarter.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[1,2,3,4,5].map(level => (
              <div key={level} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">{range}{level} — {PROFILE_BY_LEVEL[level]}</Label>
                  <Button size="sm" variant="ghost" className="print:hidden" onClick={() => saveCommentary.mutate(level)}>
                    <Save className="h-3 w-3 mr-1" />Save
                  </Button>
                </div>
                <Textarea
                  className="print:border-0 print:resize-none print:p-0"
                  rows={3}
                  placeholder="Commentary for the Board…"
                  value={localCommentary[level] ?? ''}
                  onChange={e => setLocalCommentary({ ...localCommentary, [level]: e.target.value })}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground print:text-[10px]">
          Distribution: Investment team (review &amp; sign-off) and Compliance (record).
          Auto-emailed on the 1st of Jan, Apr, Jul and Oct.
        </p>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          nav, header, .print\\:hidden { display: none !important; }
          .board-pack { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
